import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { BeforeAfterSlider } from '@/components/custom/BeforeAfterSlider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminGallery = () => {
  const { getAuthConfig } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    before_image_url: '',
    after_image_url: '',
    project_type: '',
    location: '',
    duration: '',
    budget: '',
    surface: '',
    category: '',
    description: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/projects`,
        getAuthConfig()
      );
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Erreur lors du chargement des projets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingProject(null);
    setFormData({
      before_image_url: '',
      after_image_url: '',
      project_type: '',
      location: '',
      duration: '',
      budget: '',
      surface: '',
      category: '',
      description: '',
    });
    setShowDialog(true);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      before_image_url: project.before_image_url,
      after_image_url: project.after_image_url,
      project_type: project.project_type,
      location: project.location,
      duration: project.duration,
      budget: project.budget,
      surface: project.surface,
      category: project.category,
      description: project.description || '',
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingProject) {
        // Update existing project
        await axios.put(
          `${BACKEND_URL}/api/admin/projects/${editingProject.id}`,
          formData,
          getAuthConfig()
        );
        toast.success('Projet mis à jour avec succès');
      } else {
        // Create new project
        await axios.post(
          `${BACKEND_URL}/api/admin/projects`,
          formData,
          getAuthConfig()
        );
        toast.success('Projet créé avec succès');
      }

      await fetchProjects();
      setShowDialog(false);
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce projet?')) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/projects/${projectId}`,
        getAuthConfig()
      );
      toast.success('Projet supprimé avec succès');
      await fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#336DBF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-gallery-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#2B2B2B]">Galerie Avant/Après</h2>
        <Button
          onClick={handleCreate}
          className="bg-[#336DBF] hover:bg-[#2F62AA] text-white"
          data-testid="create-project-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un projet
        </Button>
      </div>

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <Card className="border-slate-200/70">
          <CardContent className="p-12 text-center">
            <p className="text-slate-600">Aucun projet pour le moment</p>
            <Button
              onClick={handleCreate}
              className="mt-4 bg-[#336DBF] hover:bg-[#2F62AA] text-white"
            >
              Créer le premier projet
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <Card key={project.id} className="border-slate-200/70">
              <CardContent className="p-4 space-y-4">
                {/* Before/After Slider */}
                <BeforeAfterSlider
                  beforeImage={project.before_image_url}
                  afterImage={project.after_image_url}
                  className="h-48"
                />

                {/* Project Info */}
                <div className="space-y-2">
                  <h3 className="font-semibold text-lg text-[#2B2B2B]">{project.project_type}</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
                    <div>
                      <span className="font-medium">Location:</span> {project.location}
                    </div>
                    <div>
                      <span className="font-medium">Durée:</span> {project.duration}
                    </div>
                    <div>
                      <span className="font-medium">Surface:</span> {project.surface} m²
                    </div>
                    <div>
                      <span className="font-medium">Budget:</span> {project.budget}
                    </div>
                  </div>
                  <span className="inline-block px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">
                    {project.category}
                  </span>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleEdit(project)}
                    variant="outline"
                    className="flex-1"
                    data-testid={`edit-project-${project.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                  <Button
                    onClick={() => handleDelete(project.id)}
                    variant="outline"
                    className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-project-${project.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Supprimer
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProject ? 'Modifier le projet' : 'Nouveau projet'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Image URLs */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">URL Image Avant *</label>
                <Input
                  value={formData.before_image_url}
                  onChange={(e) => setFormData({ ...formData, before_image_url: e.target.value })}
                  placeholder="https://images.unsplash.com/..."
                  required
                  data-testid="before-image-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  💡 Utilisez vos vraies photos de projets (avant rénovation)
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">URL Image Après *</label>
                <Input
                  value={formData.after_image_url}
                  onChange={(e) => setFormData({ ...formData, after_image_url: e.target.value })}
                  placeholder="https://images.unsplash.com/..."
                  required
                  data-testid="after-image-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  💡 Utilisez vos vraies photos de projets (après rénovation)
                </p>
              </div>
            </div>

            {/* Project Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Type de projet *</label>
                <Input
                  value={formData.project_type}
                  onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                  placeholder="Rénovation complète"
                  required
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Catégorie *</label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="complete">Rénovation complète</SelectItem>
                    <SelectItem value="partielle">Rénovation partielle</SelectItem>
                    <SelectItem value="carrelage">Carrelage</SelectItem>
                    <SelectItem value="sanitaires">Sanitaires</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Localisation *</label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Genève"
                  required
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Durée *</label>
                <Input
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  placeholder="3 semaines"
                  required
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Surface (m²) *</label>
                <Input
                  value={formData.surface}
                  onChange={(e) => setFormData({ ...formData, surface: e.target.value })}
                  placeholder="7"
                  required
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Budget *</label>
                <Input
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  placeholder="18'000-25'000 CHF"
                  required
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="text-sm font-medium text-slate-700">Description (optionnel)</label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Détails supplémentaires..."
              />
            </div>

            {/* Submit */}
            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-[#336DBF] hover:bg-[#2F62AA] text-white"
                data-testid="submit-project-btn"
              >
                {submitting ? 'Enregistrement...' : editingProject ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGallery;